/**
 * <ContextTimeline> — "what happened before the report", read from the
 * context the page captured automatically (`errorRing.captureContext`).
 *
 * The raw context is a JSON blob of three separate rings — breadcrumbs (what
 * the person clicked and where they went), client errors (with parsed stack
 * frames) and failed requests — plus device facts. A developer wants ONE
 * story in time order, ending at the report, so the three are merged here and
 * the device facts become a line of chips. The raw JSON stays one click away.
 */

import { useMemo, useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight, Globe, MousePointerClick, Navigation, Terminal } from 'lucide-react'

import { Pill, labelStyle, mutedText } from './ui'

interface Frame { fn?: string; file?: string; line?: number; col?: number }

type Entry =
  | { t: number; at: string; kind: 'crumb'; sub: string; message: string }
  | { t: number; at: string; kind: 'error'; sub: string; message: string; frames?: Frame[]; stack?: string; count?: number; source?: string }
  | { t: number; at: string; kind: 'call'; method: string; url: string; status: number | null; message?: string; duration?: number }

function arr(v: unknown): Record<string, unknown>[] {
  return Array.isArray(v) ? v.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object') : []
}

function ts(at: unknown): number {
  const n = typeof at === 'string' ? Date.parse(at) : NaN
  return Number.isNaN(n) ? 0 : n
}

function clock(at: string, end: number): string {
  const t = ts(at)
  if (!t) return ''
  const secs = end ? Math.round((end - t) / 1000) : NaN
  const time = new Date(t).toLocaleTimeString()
  return Number.isFinite(secs) && secs >= 0 ? `${time} (−${secs < 120 ? `${secs}s` : `${Math.round(secs / 60)}m`})` : time
}

function frameText(f: Frame): string {
  const file = (f.file || '').split('/').slice(-2).join('/')
  return `${f.fn || '(anonymous)'} — ${file}${f.line ? `:${f.line}` : ''}${f.col ? `:${f.col}` : ''}`
}

function CrumbIcon({ sub }: { sub: string }) {
  if (sub === 'click') return <MousePointerClick size={12} />
  if (sub === 'navigation') return <Navigation size={12} />
  if (sub === 'request') return <Globe size={12} />
  if (sub === 'console') return <Terminal size={12} />
  return <AlertTriangle size={12} />
}

export default function ContextTimeline({ context, reportedAt }: {
  context: Record<string, unknown>
  /** When the issue was filed — the timeline counts back from it. */
  reportedAt?: string | null
}) {
  const [showAll, setShowAll] = useState(false)
  const [openFrames, setOpenFrames] = useState<number | null>(null)

  const entries = useMemo(() => {
    const out: Entry[] = []
    for (const b of arr(context.breadcrumbs)) {
      // An error breadcrumb duplicates the error entry itself; keep the richer one.
      if (b.kind === 'error') continue
      out.push({ t: ts(b.at), at: String(b.at || ''), kind: 'crumb', sub: String(b.kind || ''), message: String(b.message || '') })
    }
    for (const e of arr(context.client_errors)) {
      out.push({
        t: ts(e.at), at: String(e.at || ''), kind: 'error', sub: String(e.kind || 'script'),
        message: String(e.message || ''), frames: arr(e.frames) as Frame[],
        stack: typeof e.stack === 'string' ? e.stack : undefined,
        count: typeof e.count === 'number' ? e.count : undefined,
        source: typeof e.source === 'string' ? e.source : undefined,
      })
    }
    for (const c of arr(context.failed_calls)) {
      out.push({
        t: ts(c.at), at: String(c.at || ''), kind: 'call', method: String(c.method || ''),
        url: String(c.url || ''), status: typeof c.status === 'number' ? c.status : null,
        message: typeof c.message === 'string' ? c.message : undefined,
        duration: typeof c.duration_ms === 'number' ? c.duration_ms : undefined,
      })
    }
    return out.sort((a, b) => a.t - b.t)
  }, [context])

  const device = (context.device && typeof context.device === 'object')
    ? context.device as Record<string, unknown> : null
  const end = ts(reportedAt) || (entries.length ? entries[entries.length - 1].t : 0)
  const LIMIT = 25
  const shown = showAll ? entries : entries.slice(-LIMIT)

  const chips: string[] = []
  for (const k of ['browser', 'viewport', 'locale', 'timezone'] as const) {
    if (typeof context[k] === 'string' && context[k]) chips.push(String(context[k]))
  }
  if (device) {
    if (device.screen) chips.push(`screen ${String(device.screen)}`)
    if (device.online === false) chips.push('OFFLINE')
    const conn = device.connection as { type?: string; downlink?: number; save_data?: boolean } | undefined
    if (conn?.type) chips.push(`${conn.type}${conn.downlink ? ` ${conn.downlink} Mb/s` : ''}${conn.save_data ? ' (data saver)' : ''}`)
    if (device.device_memory_gb) chips.push(`${String(device.device_memory_gb)} GB RAM`)
    if (device.js_heap_mb) chips.push(`JS heap ${String(device.js_heap_mb)} MB`)
    if (typeof device.page_age_s === 'number') chips.push(`page open ${Math.round(device.page_age_s / 60)} min`)
    if (device.color_scheme) chips.push(`${String(device.color_scheme)} theme`)
  }

  if (!entries.length && !chips.length) return null
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={labelStyle}>What happened before the report</div>
      {chips.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
          {chips.map((c) => <Pill key={c} tone={c === 'OFFLINE' ? 'error' : 'neutral'}>{c}</Pill>)}
        </div>
      )}
      {entries.length === 0 ? <div style={mutedText}>Nothing was recorded before the report.</div> : (
        <>
          {!showAll && entries.length > LIMIT && (
            <button type="button" onClick={() => setShowAll(true)} style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--sp-accent)',
              fontSize: '0.8225rem', marginBottom: 4,
            }}>Show {entries.length - LIMIT} earlier step(s)</button>
          )}
          <ol style={{
            listStyle: 'none', margin: 0, padding: '0 0 0 10px', borderLeft: '2px solid var(--sp-border)',
            fontSize: '0.8425rem',
          }}>
            {shown.map((e, i) => {
              const idx = showAll ? i : i + Math.max(0, entries.length - LIMIT)
              const color = e.kind === 'error' ? 'var(--sp-error)' : e.kind === 'call' ? 'var(--sp-warning)' : 'var(--sp-muted)'
              return (
                <li key={idx} style={{ display: 'flex', gap: 6, padding: '2px 0', alignItems: 'flex-start' }}>
                  <span style={{ color, marginTop: 2, flexShrink: 0 }}>
                    {e.kind === 'crumb' ? <CrumbIcon sub={e.sub} /> : e.kind === 'call' ? <Globe size={12} /> : <AlertTriangle size={12} />}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ ...mutedText, fontSize: '0.8125rem', marginRight: 6 }}>{clock(e.at, end)}</span>
                    {e.kind === 'crumb' && <><strong>{e.sub}</strong> {e.message}</>}
                    {e.kind === 'call' && (
                      <>
                        <strong style={{ color }}>{e.status ?? 'no response'}</strong>{' '}
                        {e.method && e.method !== '?' ? `${e.method} ` : ''}<code>{e.url}</code>
                        {typeof e.duration === 'number' ? <span style={mutedText}> · {e.duration} ms</span> : null}
                        {e.message && <div style={{ ...mutedText, whiteSpace: 'pre-wrap' }}>{e.message}</div>}
                      </>
                    )}
                    {e.kind === 'error' && (
                      <>
                        <strong style={{ color }}>{e.sub}</strong> {e.message}
                        {e.count && e.count > 1 ? <span style={mutedText}> ×{e.count}</span> : null}
                        {(e.frames?.length || e.stack) && (
                          <button type="button" onClick={() => setOpenFrames(openFrames === idx ? null : idx)} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 2, marginLeft: 6, padding: 0,
                            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--sp-accent)', fontSize: '0.8125rem',
                          }}>
                            {openFrames === idx ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                            {e.frames?.length ? `${e.frames.length} frame(s)` : 'stack'}
                          </button>
                        )}
                        {openFrames === idx && (
                          <pre style={{
                            margin: '3px 0 0', fontSize: '0.8125rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                            background: 'var(--sp-bg2)', padding: '0.35rem 0.5rem', borderRadius: 6,
                          }}>
                            {e.frames?.length ? e.frames.map(frameText).join('\n') : e.stack}
                          </pre>
                        )}
                      </>
                    )}
                  </div>
                </li>
              )
            })}
            <li style={{ display: 'flex', gap: 6, padding: '2px 0', fontWeight: 700, color: 'var(--sp-accent)' }}>
              ● Reported{reportedAt ? ` — ${new Date(reportedAt).toLocaleTimeString()}` : ''}
            </li>
          </ol>
        </>
      )}
    </div>
  )
}
